import { useEffect, useMemo, useRef, useState } from "react";
import { Send } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitCheckbox } from "@/app/routes/dashboard/components/kit/Checkbox";
import { KitSwitch } from "@/app/routes/dashboard/components/kit/Switch";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useGetBrands } from "@/hooks/useGetBrands";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { cn } from "@/utils/helpers";
import { useClientNotifyState, useNotifyClients } from "../api/useContentCalendar";
import {
  DayField,
  DialogLabel,
  DialogNotice,
  Initials,
  ReviewDialog,
  dayLabel,
  localTodayIso,
} from "../review/reviewDialogParts";

/** "post": the concept and its creative brief together, answered once (Figma 08). */
export type SendToClientKind = "concept" | "brief" | "post";

export interface SendToClientItem {
  slotId: string;
  title: string;
  /** Compact context such as publish date, format, or channel. */
  meta?: string | null;
  versionLabel?: string | null;
  /** The post's day (ISO); drawn as the date column and used for the answer-by default. */
  scheduledAt?: string | null;
}

/** What the send asks of the client (backend #130). */
export interface SendToClientOptions {
  /** YYYY-MM-DD, or null when no date is asked. */
  answerBy: string | null;
  note: string | null;
  /** The combined send emails in the same request: whether, and to whom. */
  notify?: boolean;
  notifyUserIds?: string[];
}

/** What a combined send answers about its email (the server sends it). */
export interface SendToClientOutcome {
  email?: { notified: number; failed: number; failures: { userId: string; email: string }[] } | null;
}

interface SendToClientModalProps {
  isOpen: boolean;
  planId: string | null;
  kind: SendToClientKind;
  items: SendToClientItem[];
  /** Defaults to every item. Useful when opening the same batch with one item preselected. */
  initialSelectedSlotIds?: string[];
  busy?: boolean;
  onPublish: (slotIds: string[], options: SendToClientOptions) => Promise<void | SendToClientOutcome> | void;
  /** Settings, Calendar, Reply by: days before the first post. Undefined keeps nine; null asks no date. */
  replyByDaysBefore?: number | null;
  onClose: () => void;
  /** Opens the brand's guest list (the client's accounts). */
  onManageGuests?: () => void;
  /** The client's name for the title and labels ("Send to Fatafeat"); the brand's name. */
  clientName?: string | null;
  /**
   * Whether the caller forwards the answer-by day and the note. The concept
   * send does; the brief send's hook does not yet, so its dialog hides them.
   */
  withAnswerBy?: boolean;
}

function errorText(error: unknown): string {
  const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
  if (message) return message;
  if (error instanceof Error && error.message.trim()) return error.message;
  return "Please try again.";
}

function plural(kind: SendToClientKind, count: number): string {
  if (kind === "post") return count === 1 ? "Post" : "Posts";
  const noun = kind === "concept" ? "concept" : "creative brief";
  return `${noun}${count === 1 ? "" : "s"}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** The day part of an ISO stamp, YYYY-MM-DD (calendar days are UTC midnights). */
function isoDay(iso: string): string {
  return iso.slice(0, 10);
}

function addDays(day: string, days: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!) + days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The answer-by default: production needs the week before the first post, so
 * nine days before it (Thu 10 Dec → Tue 1 Dec), never before today.
 */
function defaultAnswerBy(firstDay: string | null, today: string, daysBefore: number | null = 9): string {
  if (!firstDay || daysBefore === null) return "";
  const wanted = addDays(firstDay, -daysBefore);
  return wanted < today ? today : wanted;
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** "Noura", "Noura and Sara", "Noura, Sara and Omar". */
function nameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * Send to the client (library Calendar / Send item row, Recipient chip, Send ·
 * Phone; chapter 08 frames pDm4v and xaubY).
 *
 * Concepts and creative briefs still go out as two sends: the backend has no
 * joint send. Publishing and emailing remain separate backend actions; once
 * publication succeeds, an email retry never publishes the work a second time.
 * The email goes to the brand's guests — the client's accounts.
 */
export default function SendToClientModal({
  isOpen,
  planId,
  kind,
  items,
  initialSelectedSlotIds,
  busy = false,
  onPublish,
  onClose,
  onManageGuests,
  clientName,
  withAnswerBy = kind !== "brief",
  replyByDaysBefore,
}: SendToClientModalProps) {
  const combined = kind === "post";
  const replyDays = replyByDaysBefore === undefined ? 9 : replyByDaysBefore;
  const phone = useMediaQuery("(max-width: 767px)");
  const notifyState = useClientNotifyState(planId, isOpen);
  const notifyClients = useNotifyClients(planId);
  // The client is the brand: its name unless the caller says otherwise.
  const selectedBrandId = useWorkspaceStore((s) => s.selectedBrandId);
  const { data: brands } = useGetBrands();
  const brandName = brands?.find((brand) => brand.id === selectedBrandId)?.name?.trim() || null;
  const clientLabel = clientName?.trim() || brandName;
  const client = clientLabel || "the client";
  const clientShort = clientLabel || "Client";
  const defaultSelection = (() => {
    const requested = new Set(initialSelectedSlotIds ?? items.map((item) => item.slotId));
    return items.filter((item) => requested.has(item.slotId)).map((item) => item.slotId);
  })();
  const defaultSelectionKey = JSON.stringify(defaultSelection);
  const wasOpen = useRef(false);
  const [displayItems, setDisplayItems] = useState<SendToClientItem[]>(items);
  const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>(defaultSelection);
  const [emailGuests, setEmailGuests] = useState(true);
  const [selectedGuestIds, setSelectedGuestIds] = useState<string[]>([]);
  const [recipientSelectionSeeded, setRecipientSelectionSeeded] = useState(false);
  const [answerBy, setAnswerBy] = useState("");
  const [note, setNote] = useState("");
  const [published, setPublished] = useState(false);
  const [retryGuestIds, setRetryGuestIds] = useState<string[]>([]);
  const [step, setStep] = useState<"idle" | "publishing" | "emailing">("idle");
  const [notice, setNotice] = useState<{ tone: "error" | "warning"; title: string; detail: string } | null>(null);

  const guests = useMemo(() => notifyState.data?.guests ?? [], [notifyState.data?.guests]);
  const working = busy || step !== "idle";
  const selectedCount = selectedSlotIds.length;
  const retryingEmail = published && retryGuestIds.length > 0;
  const today = localTodayIso();

  // The first post among those going, for the answer-by default and its line.
  const firstDay = useMemo(() => {
    const days = displayItems
      .filter((item) => selectedSlotIds.includes(item.slotId) && item.scheduledAt)
      .map((item) => isoDay(item.scheduledAt as string))
      .sort();
    return days[0] ?? null;
  }, [displayItems, selectedSlotIds]);

  useEffect(() => {
    const opening = isOpen && !wasOpen.current;
    wasOpen.current = isOpen;
    if (!opening) return;
    const selection = JSON.parse(defaultSelectionKey) as string[];
    setDisplayItems(items);
    setSelectedSlotIds(selection);
    setEmailGuests(true);
    setSelectedGuestIds([]);
    setRecipientSelectionSeeded(false);
    const first =
      items
        .filter((item) => selection.includes(item.slotId) && item.scheduledAt)
        .map((item) => isoDay(item.scheduledAt as string))
        .sort()[0] ?? null;
    setAnswerBy(defaultAnswerBy(first, localTodayIso(), replyDays));
    setNote("");
    setPublished(false);
    setRetryGuestIds([]);
    setStep("idle");
    setNotice(null);
  }, [defaultSelectionKey, isOpen, items, replyDays]);

  // Everyone is ticked once the guests arrive.
  useEffect(() => {
    if (!isOpen || recipientSelectionSeeded || guests.length === 0) return;
    setSelectedGuestIds(guests.map((guest) => guest.userId));
    setRecipientSelectionSeeded(true);
  }, [guests, isOpen, recipientSelectionSeeded]);

  const toggleSlot = (slotId: string, selected: boolean) => {
    setSelectedSlotIds((current) =>
      selected ? Array.from(new Set([...current, slotId])) : current.filter((id) => id !== slotId),
    );
  };

  const toggleGuest = (userId: string, selected: boolean) => {
    setRecipientSelectionSeeded(true);
    setSelectedGuestIds((current) =>
      selected ? Array.from(new Set([...current, userId])) : current.filter((id) => id !== userId),
    );
  };

  const notify = async (userIds: string[]) => {
    setStep("emailing");
    try {
      const result = await notifyClients.mutateAsync(userIds);
      if (result.failed > 0) {
        setRetryGuestIds(result.failures.map((failure) => failure.userId));
        setNotice({
          tone: "warning",
          title: `${plural(kind, selectedCount)} sent; ${result.failed} email${result.failed === 1 ? "" : "s"} failed`,
          detail: `Nothing will be sent twice. Check ${result.failures.map((failure) => failure.email).join(", ")} and retry the email only.`,
        });
        return;
      }
      onClose();
    } catch (error) {
      setRetryGuestIds(userIds);
      setNotice({
        tone: "warning",
        title: `${plural(kind, selectedCount)} sent, but the email did not go`,
        detail: `${errorText(error)} You can retry the email without sending again.`,
      });
    } finally {
      setStep("idle");
    }
  };

  const recipients = guests.filter((guest) => selectedGuestIds.includes(guest.userId));
  const willEmail = emailGuests && guests.length > 0;

  const submit = async () => {
    if (working) return;
    if (retryingEmail) {
      await notify(retryGuestIds);
      return;
    }
    if (selectedCount === 0 || !planId) return;

    setNotice(null);
    setStep("publishing");
    let outcome: void | SendToClientOutcome = undefined;
    try {
      outcome = await onPublish(selectedSlotIds, {
        answerBy: withAnswerBy && answerBy ? answerBy : null,
        note: withAnswerBy && note.trim() ? note.trim() : null,
        ...(combined && willEmail && recipients.length > 0
          ? { notify: true, notifyUserIds: recipients.map((guest) => guest.userId) }
          : {}),
      });
      setPublished(true);
    } catch (error) {
      setNotice({ tone: "error", title: "Nothing was sent", detail: errorText(error) });
      setStep("idle");
      return;
    }

    // The combined send emailed in the same request: only a failed address is left to retry.
    if (combined) {
      const email = outcome && "email" in outcome ? outcome.email : null;
      setStep("idle");
      if (email && email.failed > 0) {
        setRetryGuestIds(email.failures.map((failure) => failure.userId));
        setNotice({
          tone: "warning",
          title: `${plural(kind, selectedCount)} sent; ${email.failed} email${email.failed === 1 ? "" : "s"} failed`,
          detail: `Nothing will be sent twice. Check ${email.failures.map((failure) => failure.email).join(", ")} and retry the email only.`,
        });
        return;
      }
      onClose();
      return;
    }

    if (!willEmail || recipients.length === 0) {
      setStep("idle");
      onClose();
      return;
    }
    await notify(recipients.map((guest) => guest.userId));
  };

  const primaryLabel = retryingEmail
    ? `Retry ${retryGuestIds.length} email${retryGuestIds.length === 1 ? "" : "s"}`
    : combined
      ? `Send to ${clientShort === "Client" ? "the client" : clientShort}`
      : "Send";
  const description = combined
    ? phone
      ? "They review the concept and the brief together, and answer once."
      : "They review the concept and the creative brief together, comment on any part, and answer once."
    : kind === "concept"
      ? phone
        ? "They review the concept and answer it."
        : "They review the concept, comment on any part, and answer it. The creative brief follows once they approve."
      : phone
        ? "They review the creative brief and answer it."
        : "They review the creative brief, comment on any part, and answer it.";
  const emailNames = nameList(recipients.map((guest) => firstName(guest.name || guest.email)));

  const lockedItems = working || published;

  const itemRows = (
    <div className="flex flex-col gap-2">
      <DialogLabel phone={phone} aside={`${selectedCount} ready`}>
        What goes
      </DialogLabel>
      <div className="flex max-h-[17rem] flex-col gap-2 overflow-y-auto">
        {displayItems.map((item) => {
          const picked = selectedSlotIds.includes(item.slotId);
          const day = item.scheduledAt ? new Date(item.scheduledAt) : null;
          const sub = [item.meta, item.versionLabel].filter(Boolean).join(" · ");
          return (
            <KitCheckbox
              key={item.slotId}
              isSelected={picked}
              isDisabled={lockedItems}
              onChange={(selected) => toggleSlot(item.slotId, selected)}
              aria-label={item.title}
              className={cn(
                "flex w-full !items-center rounded-card",
                phone ? "gap-3 bg-co-surface-solid p-3" : "gap-3 bg-co-surface px-3 py-2.5 shadow-[inset_0_0_0_1px_var(--co-line)]",
              )}
            >
              <span className="flex min-w-0 flex-1 items-center gap-3">
                {day ? (
                  <span className={cn("flex shrink-0 flex-col items-center", phone ? "w-8" : "w-9")}>
                    <span
                      className={cn(
                        "font-section leading-none text-co-text",
                        phone ? "text-[16px] font-extrabold" : "text-[20px] font-bold",
                      )}
                    >
                      {day.getUTCDate()}
                    </span>
                    <span
                      className={cn(
                        "mt-0.5 font-mono text-[9.5px] text-co-muted",
                        phone ? "tracking-[0.11em]" : "uppercase tracking-[0.13em]",
                      )}
                    >
                      {day.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" })}
                    </span>
                  </span>
                ) : null}
                <span className={cn("flex min-w-0 flex-1 flex-col", phone ? "gap-0.5" : "gap-[3px]")}>
                  <span
                    className={cn(
                      "font-semibold text-co-text",
                      phone ? "text-[13.5px] leading-[1.35]" : "text-[14px] leading-[1.4]",
                    )}
                  >
                    {item.title}
                  </span>
                  {sub ? (
                    <span className={cn("text-co-muted", phone ? "text-[12px]" : "text-[12.5px]")}>{sub}</span>
                  ) : null}
                </span>
              </span>
            </KitCheckbox>
          );
        })}
      </div>
    </div>
  );

  const answerBlock = withAnswerBy ? (
    <>
      <div className="flex flex-col gap-2">
        <DialogLabel phone={phone}>{combined ? "Reply by" : "Answer by"}</DialogLabel>
        {phone ? (
          <>
            <DayField
              label="Answer by"
              value={answerBy}
              onChange={setAnswerBy}
              minValue={today}
              icon={false}
              placeholder="No date"
            />
            {firstDay ? <p className="text-[12px] leading-[1.5] text-co-muted">first post {dayLabel(firstDay)}</p> : null}
          </>
        ) : (
          <div className="flex items-center gap-3">
            <DayField
              label="Answer by"
              value={answerBy}
              onChange={setAnswerBy}
              minValue={today}
              placeholder="No date"
              className="w-[190px] shrink-0"
            />
            <p className="min-w-0 flex-1 text-[12.5px] leading-[1.45] text-co-muted">
              {combined
                ? replyDays === null
                  ? `No reply-by day in ${clientShort}’s settings. Pick one for this send if you want an answer by then.`
                  : `${replyDays} ${replyDays === 1 ? "day" : "days"} before the first post, from ${clientShort}’s settings. Change it for this send only.`
                : firstDay
                  ? `${dayLabel(firstDay)} goes live first, and production needs the week before it. ${clientShort} sees this date on every post.`
                  : `${clientShort} sees this date on every post.`}
            </p>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <DialogLabel phone={phone} aside="Optional">
          Note for {clientShort}
        </DialogLabel>
        {phone ? (
          <input
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Add a note…"
            aria-label={`Note for ${clientShort}`}
            className="co-input-own-focus co-glass-control h-11 w-full rounded-card px-3.5 text-[14px] text-co-text caret-co-accent placeholder:text-co-faint outline-offset-0 focus:outline-2 focus:outline-co-accent"
          />
        ) : (
          <textarea
            value={note}
            maxLength={500}
            rows={1}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Add a note…"
            aria-label={`Note for ${clientShort}`}
            className="co-input-own-focus co-glass-control field-sizing-content w-full resize-none rounded-card px-3.5 py-3 text-[14px] leading-[1.45] text-co-text caret-co-accent placeholder:text-co-faint outline-offset-0 focus:outline-2 focus:outline-co-accent"
          />
        )}
      </div>
    </>
  ) : null;

  const guestRows = notifyState.isLoading ? (
    <div className={cn("flex gap-2", phone && "flex-col")} aria-label="Loading recipients">
      {[0, 1].map((n) => (
        <span key={n} className={cn("animate-pulse bg-co-tonal", phone ? "h-11 rounded-tip" : "h-10 w-32 rounded-pill")} />
      ))}
    </div>
  ) : notifyState.isError ? (
    <div role="alert" className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-[12.5px] text-co-muted">Recipients did not load. You can still send without an email.</p>
      <KitButton variant="ghost" onClick={() => void notifyState.refetch()}>
        Retry
      </KitButton>
    </div>
  ) : guests.length === 0 ? (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-[12.5px] text-co-muted">This brand has no client accounts yet. Invite the client to email them.</p>
      {onManageGuests ? (
        <KitButton variant="ghost" onClick={onManageGuests}>
          Manage clients
        </KitButton>
      ) : null}
    </div>
  ) : (
    <div className={cn("flex gap-2", phone ? "flex-col" : "flex-wrap")}>
      {guests.map((guest) => {
        const name = guest.name?.trim() || guest.email;
        return (
          <KitCheckbox
            key={guest.userId}
            isSelected={selectedGuestIds.includes(guest.userId)}
            isDisabled={working || published || !emailGuests}
            onChange={(selected) => toggleGuest(guest.userId, selected)}
            aria-label={name}
            className={cn(
              "bg-co-tonal",
              phone ? "flex w-full gap-2.5 rounded-tip px-3 py-2.5" : "h-10 gap-2 rounded-pill pl-2.5 pr-3.5",
            )}
          >
            <span className={cn("flex min-w-0 items-center", phone ? "gap-2.5" : "gap-2")}>
              <Initials name={name} fontSize={phone ? 12 : 10} />
              <span className={cn("truncate text-co-text", phone ? "text-[13.5px] font-semibold" : "text-[13.5px] font-medium")}>
                {firstName(name)}
              </span>
              {clientLabel ? (
                phone ? (
                  <span className="truncate text-[12px] text-co-muted">{clientLabel}</span>
                ) : (
                  <span className="truncate font-mono text-[9.5px] uppercase tracking-[0.13em] text-co-muted">{clientLabel}</span>
                )
              ) : null}
            </span>
          </KitCheckbox>
        );
      })}
    </div>
  );

  const emailToggle =
    guests.length === 0 ? null : phone ? (
      <KitCheckbox
        isSelected={emailGuests}
        isDisabled={working || published}
        onChange={setEmailGuests}
        className="text-[13px] text-co-text2"
      >
        Email them now · On for a new version.
      </KitCheckbox>
    ) : (
      <div className="flex items-center gap-3 pt-2">
        <KitSwitch
          checked={emailGuests}
          onChange={setEmailGuests}
          disabled={working || published}
          label={emailNames ? `Email ${emailNames} now` : "Email them now"}
        />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[13.5px] font-semibold text-co-text">
            {emailNames ? `Email ${emailNames} now` : "Email them now"}
          </span>
          <span className="text-[12.5px] leading-[1.45] text-co-muted">
            {combined
              ? "On: sends a new version. Off: small updates only."
              : "On for a new version. Turn it off for small updates; their private page updates either way."}
          </span>
        </span>
      </div>
    );

  const disabled =
    !planId || (!retryingEmail && selectedCount === 0) || (!retryingEmail && willEmail && recipients.length === 0);

  const footer = phone ? (
    <div className="shrink-0 px-4 pb-4">
      <KitButton
        size="lg"
        className="h-12 w-full"
        onClick={submit}
        loading={working}
        isDisabled={disabled}
        iconRight={Send}
      >
        {primaryLabel}
      </KitButton>
    </div>
  ) : (
    <div className="flex shrink-0 items-center justify-end gap-3 px-6 pb-6 pt-4">
      {combined ? <p className="mr-auto text-[13px] text-co-muted">Sending is your approval.</p> : null}
      <KitButton variant="ghost" onClick={onClose} isDisabled={working}>
        {published ? "Close" : "Cancel"}
      </KitButton>
      <KitButton onClick={submit} loading={working} isDisabled={disabled}>
        {primaryLabel}
      </KitButton>
    </div>
  );

  return (
    <ReviewDialog
      isOpen={isOpen}
      onOpenChange={(open) => !open && !working && onClose()}
      title={`Send to ${clientShort === "Client" ? "the client" : clientShort}`}
      description={description}
      phone={phone}
      footer={footer}
      dismissable={!working}
    >
      <div className={cn("flex flex-col", phone ? "gap-4 p-4" : "gap-5 px-6 pb-5 pt-2")}>
        {itemRows}
        {answerBlock}
        <div className="flex flex-col gap-2">
          {/* Figma 08 (13 Oct): "Goes to Fatafeat · any of Fatafeat's people can approve". The rows pick who is emailed. */}
          <DialogLabel phone={phone}>Goes to {client} · any of {client}’s people can approve</DialogLabel>
          {guestRows}
          {emailToggle}
        </div>
        {notice ? <DialogNotice {...notice} /> : null}
        {published && !notice ? (
          <p className="text-[12.5px] text-co-muted">The work is already on {client}’s page.</p>
        ) : null}
      </div>
    </ReviewDialog>
  );
}

